import {
    ComponentPropsWithoutRef,
    ForwardedRef,
    ReactNode,
    forwardRef,
    useContext,
    useMemo,
} from 'react';
import { Column, Header, Table as ITable } from '@tanstack/react-table';
import * as Draggable from '@givebutter/primitives/draggable';
import {
    ITableHeaderContext,
    TableContext,
    TableHeaderContext,
} from 'components/table/TableContext';
import { TableHeaderDataActionable } from 'components/table/TableHeaderDataActionable';
import { TableHeaderSelectAllRow } from 'components/table/TableHeaderSelectAllRow';
import { TableRow } from 'components/table/TableRow';
import { twMerge } from 'tailwind-merge';

interface TableHeaderProps extends Omit<ComponentPropsWithoutRef<'thead'>, 'children'> {}

const TableHeader = forwardRef(
    (props: TableHeaderProps, ref: ForwardedRef<HTMLTableSectionElement>) => {
        const { table } = useContext(TableContext);
        const { className = '', ...rest } = props;
        const canSelectAllRecords = !!table.setIsAllRecordsSelected;

        return (
            <thead
                ref={ref}
                className={twMerge(`whitespace-nowrap px-4 pb-4 pt-2 text-sm`, className)}
                {...rest}
            >
                {table.getHeaderGroups().map(headerGroup => (
                    <Draggable.Root
                        key={headerGroup.id}
                        modifiers={[]}
                        onDragEnd={e => {
                            const { active, over } = e;
                            const headers = headerGroup.headers;
                            const shouldReorder = over && over.id !== active.id;

                            if (shouldReorder) {
                                const overHeader = headers.find(
                                    h => h.id === (over.id as string),
                                )?.column;
                                const activeHeader = headers.find(
                                    h => h.id === (active.id as string),
                                )?.column;

                                if (!!overHeader && !!activeHeader) {
                                    if (haltIfDifferentPinType(overHeader, activeHeader)) {
                                        return;
                                    }

                                    if (isSamePinType(overHeader, activeHeader)) {
                                        handleDraggedPinnedColumns(table, overHeader, activeHeader);
                                        return;
                                    }

                                    handleDraggedColumns(table, headers, overHeader, activeHeader);
                                }
                            }
                        }}
                    >
                        <TableRow>
                            {headerGroup.headers.map(header => (
                                <OptimizedHeaderProvider key={header.id} header={header}>
                                    <TableHeaderDataActionable />
                                </OptimizedHeaderProvider>
                            ))}
                        </TableRow>
                    </Draggable.Root>
                ))}
                {canSelectAllRecords && <TableHeaderSelectAllRow />}
            </thead>
        );
    },
);

const OptimizedHeaderProvider = <T, K>({
    header,
    children,
}: ITableHeaderContext<T, K> & { children: ReactNode }) => {
    const providerProps = useMemo(() => ({ header }), [header]);

    return (
        <TableHeaderContext.Provider value={providerProps}>{children}</TableHeaderContext.Provider>
    );
};

const haltIfDifferentPinType = <T,>(col1: Column<T>, col2: Column<T>) =>
    col1.getIsPinned() !== col2.getIsPinned();

const isSamePinType = <T,>(col1: Column<T>, col2: Column<T>) =>
    col1.getIsPinned() && col2.getIsPinned();

const handleDraggedPinnedColumns = <T,>(table: ITable<T>, col1: Column<T>, col2: Column<T>) => {
    table.setColumnPinning(old => {
        const nextColumnPinning = [...(old.left ? old.left : [])];
        const overHeaderIndex = nextColumnPinning.indexOf(col1.id as string);
        const activeHeaderIndex = nextColumnPinning.indexOf(col2.id as string);

        const temp = nextColumnPinning[overHeaderIndex];
        nextColumnPinning[overHeaderIndex] = nextColumnPinning[activeHeaderIndex];
        nextColumnPinning[activeHeaderIndex] = temp;

        return {
            ...old,
            left: nextColumnPinning,
        };
    });
};

const handleDraggedColumns = <T,>(
    table: ITable<T>,
    headers: Header<T, unknown>[],
    col1: Column<T>,
    col2: Column<T>,
) => {
    table.setColumnOrder(() => {
        const nextOrder = (headers ?? []).map(h => h.id);
        const overHeaderIndex = nextOrder.indexOf(col1.id as string);
        const activeHeaderIndex = nextOrder.indexOf(col2.id as string);
        const temp = nextOrder[overHeaderIndex];
        nextOrder[overHeaderIndex] = nextOrder[activeHeaderIndex];
        nextOrder[activeHeaderIndex] = temp;

        return nextOrder;
    });
};

export { TableHeader };
export type { TableHeaderProps };
